import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IDeviceLogConfig } from '../../services/rc-logs.service';
import { AppSettingsService } from '@nx-fe/services';
import { IWorkPanelWindow } from 'xsmodules/remote-control/services/work-panel.service';
import {
  IAgentVideoRecordingSettings,
  IRCCanvasConfig,
  IScreenCaptureConfig,
  IVideoRecordingUserConfig,
} from 'xsmodules/remote-control/services/remote-control.interfaces';
import { ColorQuality } from 'app/modules/remote-control-ng/definitions';
import { RcContext } from 'app/modules/remote-control-ng/context/rcContext';
import { RestartThrottling } from 'app/modules/remote-control-ng/services/restartThrottling';

export interface IRemoteControlUserPreferencesService {
  /** Gets the video recording configuration */
  getVideoRecordingUserConfig(): Observable<IVideoRecordingUserConfig>;

  /** Saves the video recording configuration */
  updateVideoRecordingUserConfig(value: IVideoRecordingUserConfig): Observable<IVideoRecordingUserConfig>;

  /** Sets a function to be called whenever the video recordings settings change */
  setOnUpdateVideoRecordingPrefs(callback: (value: IVideoRecordingUserConfig) => void): void;

  /** Gets the screen capture configuration */
  getScreenCaptureConfig(): Observable<IScreenCaptureConfig>;

  /** Saves the screen capture configuration */
  updateScreenCaptureConfig(value: IScreenCaptureConfig): Observable<IScreenCaptureConfig>;

  /** Sets a function to be called whenever the screen capture settings change */
  setOnUpdateScreenCapturePrefs(callback: (value: IScreenCaptureConfig) => void): void;

  /** Emits the RC session initiated notification */
  initSession(value: IRCCanvasConfig): void;

  /** Emits the RC session ended notification */
  endSession(): void;

  /** Returns the default value for this property or, if specified, the passthru value */
  defaultRecordMouseMovement(passthru?: boolean): boolean;

  /** Returns the default value for this property or, if specified, the passthru value */
  defaultRecordMouseClicks(passthru?: boolean): boolean;

  /** Returns the default value for this property or, if specified, the passthru value */
  defaultIncludeDeviceSkin(passthru?: boolean): boolean;

  /** Returns the default value for this property or, if specified, the passthru value */
  defaultImageFormat(passthru?: string): string;

  /** Returns the default value for this property or, if specified, the passthru value */
  defaultImageQuality(passthru?: number): number;

  /** Returns the default value for this property or, if specified, the passthru value */
  defaultBitDepth(passthru?: number): number;

  /** Saves the agent video recording configuration */
  updateAgentVideoRecordingPrefs(value: IAgentVideoRecordingSettings): Observable<IAgentVideoRecordingSettings>;

  /** Sets a function to be called whenever the agent video recording settings change */
  setOnUpdateAgentVideoRecordingPrefs(callback: (value: IAgentVideoRecordingSettings) => void): void;

  /** Accepts partial agent video recording settings and returns fully initialized
   * settings populating missing parameters with the defaults. */
  defaultAgentVideoRecordingPrefs(partial?: IAgentVideoRecordingSettings): IAgentVideoRecordingSettings;

  /** Gets the RC Screen Canvas settings */
  getRCCanvasConfig(): Observable<IRCCanvasConfig>;

  /** Set the RC Canvas setting and store it in database */
  updateRCCanvasConfig(value: IRCCanvasConfig): Observable<IRCCanvasConfig>;

  setRCCanvasParameters(rcCanvasConfig: IRCCanvasConfig): void;

  /** Sets a function to be called whenever the RC Canvas settings change */
  setOnUpdateRCCanvasPrefs(callback: (value: IRCCanvasConfig) => void): void;

  /** Returns the default value for this property or, if specified, the passthru value */
  defaultRCColourQuality(passthru?: string): string;

  /** Returns the default value for this property or, if specified, the passthru value */
  defaultRCImageQuality(passthru?: number): number;

  /** Gets the Device Log type settings */
  getDeviceLogTypeConfig(): Observable<IDeviceLogConfig>;

  /** Set the Device Log Type setting and store it in database */
  updateDeviceLogTypeConfig(value: IDeviceLogConfig): Observable<IDeviceLogConfig>;

  /** Sets a function to be called whenever the Device Log Type settings change */
  setOnUpdateDeviceLogTypePrefs(callback: (value: IDeviceLogConfig) => void): void;

  /** Returns the default value for this property or, if specified, the passthru value */
  defaultDeviceLogReportType(passthru?: number): number;

  /** Returns the default value for this property or, if specified, the passthru value */
  defaultDeviceLogCategory(passthru?: string): string;
}

/** Service for fetching and saving user preferences for remote control */
@Injectable()
export class RemoteControlUserPreferencesService implements IRemoteControlUserPreferencesService {
  private onUpdateVideoRecordingPrefs: (value: IVideoRecordingUserConfig) => void;
  private onUpdateScreenCapturePrefs: (value: IScreenCaptureConfig) => void;
  private onUpdateAgentVideoRecordingPrefs: (value: IAgentVideoRecordingSettings) => void;
  private onUpdateRCCanvasPrefs: (value: IRCCanvasConfig) => void;
  private onUpdateDeviceLogTypePrefs: (value: IDeviceLogConfig) => void;

  // Emitted when the RC session initiates
  public sessionInitiated: Subject<void>;

  // Emitted when the RC session ends
  public sessionEnded: Subject<void>;
  private window: IWorkPanelWindow = window as any;
  private restartThrottle: RestartThrottling;

  constructor(
    private http: HttpClient,
    private appSettings: AppSettingsService,
  ) {
    this.sessionInitiated = new Subject<void>();
    this.sessionEnded = new Subject<void>();
    this.restartThrottle = inject(RestartThrottling);
  }

  /** Gets the video recording configuration */
  public getVideoRecordingUserConfig = (): Observable<IVideoRecordingUserConfig> => {
    const value = this.http.get<IVideoRecordingUserConfig>(
      this.appSettings.remoteControlServiceUrl + 'config/videorecording'
    );
    return value.pipe(
      tap(result => {
        if (this.onUpdateVideoRecordingPrefs) {
          this.onUpdateVideoRecordingPrefs(result);
        }
      })
    );
  };

  /** Saves the video recording configuration */
  public updateVideoRecordingUserConfig = (value: IVideoRecordingUserConfig): Observable<IVideoRecordingUserConfig> => {
    if (this.onUpdateVideoRecordingPrefs) {
      this.onUpdateVideoRecordingPrefs(value);
    }
    return this.http.put<IVideoRecordingUserConfig>(
      this.appSettings.remoteControlServiceUrl + 'config/videorecording',
      value
    );
  };

  /** Gets the screen capture configuration */
  public getScreenCaptureConfig = (): Observable<IScreenCaptureConfig> => {
    const value = this.http.get<IScreenCaptureConfig>(
      this.appSettings.remoteControlServiceUrl + 'config/screencapture'
    );
    return value.pipe(
      tap(result => {
        if (this.onUpdateScreenCapturePrefs) {
          this.onUpdateScreenCapturePrefs(result);
        }
      })
    );
  };

  /** Saves the screen capture configuration */
  public updateScreenCaptureConfig = (value: IScreenCaptureConfig): Observable<IScreenCaptureConfig> => {
    if (this.onUpdateScreenCapturePrefs) {
      this.onUpdateScreenCapturePrefs(value);
    }
    return this.http.put<IScreenCaptureConfig>(
      this.appSettings.remoteControlServiceUrl + 'config/screencapture',
      value
    );
  };

  /** Gets the RC Screen Canvas settings */
  public getRCCanvasConfig = (): Observable<IRCCanvasConfig> => {
    const value = this.http.get<IRCCanvasConfig>(this.appSettings.remoteControlServiceUrl + 'config/screenSettings');
    return value.pipe(
      tap(result => {
        if (this.onUpdateRCCanvasPrefs) {
          this.onUpdateRCCanvasPrefs(result);
        }
      })
    );
  };

  /** Set the RC Canvas setting and store it in database */
  public updateRCCanvasConfig = (value: IRCCanvasConfig): Observable<IRCCanvasConfig> => {
    // Set the RC Canvas Parameters
    this.setRCCanvasParameters(value);
    // Soft RC session update, restart not forced
    if (RcContext.instance.deviceContext.videoStreaming) {
      this.restartThrottle.applyChange(undefined, RcContext.instance);
    } else {
      this.restartThrottle.send(undefined, undefined, RcContext.instance);
    }

    if (this.onUpdateRCCanvasPrefs) {
      this.onUpdateRCCanvasPrefs(value);
    }
    return this.http.put<IRCCanvasConfig>(this.appSettings.remoteControlServiceUrl + 'config/screenSettings', value);
  };

  /** Gets the Device Log Type settings */
  public getDeviceLogTypeConfig = (): Observable<IDeviceLogConfig> => {
    const value = this.http.get<IDeviceLogConfig>(
      this.appSettings.remoteControlServiceUrl + 'config/devicelogSettings'
    );
    return value.pipe(
      tap(result => {
        if (this.onUpdateDeviceLogTypePrefs) {
          this.onUpdateDeviceLogTypePrefs(result);
        }
      })
    );
  };

  /** Set the Device Log Type setting and store it in database */
  public updateDeviceLogTypeConfig = (value: IDeviceLogConfig): Observable<IDeviceLogConfig> => {
    // Set the Device Log Type Parameters
    if (this.onUpdateDeviceLogTypePrefs) {
      this.onUpdateDeviceLogTypePrefs(value);
    }
    return this.http.put<IDeviceLogConfig>(
      this.appSettings.remoteControlServiceUrl + 'config/devicelogSettings',
      value
    );
  };

  /** Sets a function to be called whenever the video recordings settings change */
  public setOnUpdateVideoRecordingPrefs = (callback: (value: IVideoRecordingUserConfig) => void): void => {
    this.onUpdateVideoRecordingPrefs = callback;
  };

  /** Sets a function to be called whenever the screen capture settings change */
  public setOnUpdateScreenCapturePrefs = (callback: (value: IScreenCaptureConfig) => void): void => {
    this.onUpdateScreenCapturePrefs = callback;
  };

  /** Sets a function to be called whenever the RC Canvas settings change */
  public setOnUpdateRCCanvasPrefs = (callback: (value: IRCCanvasConfig) => void): void => {
    this.onUpdateRCCanvasPrefs = callback;
  };

  /** Sets a function to be called whenever the Device Log Type settings change */
  public setOnUpdateDeviceLogTypePrefs = (callback: (value: IDeviceLogConfig) => void): void => {
    this.onUpdateDeviceLogTypePrefs = callback;
  };

  /** Emits the RC session initiated notification */
  /* istanbul ignore next */
  public initSession = (value: IRCCanvasConfig): void => {
    // Set the RC Canvas Parameters
    this.setRCCanvasParameters(value);

    this.sessionInitiated.next();
  };

  /** Emits the RC session ended notification */
  /* istanbul ignore next */
  public endSession = (): void => {
    this.sessionEnded.next();
  };

  /** Returns the default value for this property or, if specified, the passthru value */
  public defaultRecordMouseMovement = (passthru?: boolean): boolean =>
    passthru === undefined || passthru === null ? true : passthru;

  /** Returns the default value for this property or, if specified, the passthru value */
  public defaultRecordMouseClicks = (passthru?: boolean): boolean =>
    passthru === undefined || passthru === null ? true : passthru;

  /** Returns the default value for this property or, if specified, the passthru value */
  public defaultIncludeDeviceSkin = (passthru?: boolean): boolean =>
    passthru === undefined || passthru === null ? true : passthru;

  /** Returns the default value for this property or, if specified, the passthru value */
  public defaultImageFormat = (passthru?: string): string =>
    passthru === undefined || passthru === null ? 'PNG' : passthru;

  /** Returns the default value for this property or, if specified, the passthru value */
  public defaultImageQuality = (passthru?: number): number =>
    passthru === undefined || passthru === null ? 80 : passthru;

  /** Returns the default value for this property or, if specified, the passthru value */
  public defaultBitDepth = (passthru?: number): number => (passthru === undefined || passthru === null ? 24 : passthru);

  /** Returns the default value for this property or, if specified, the passthru value */
  public defaultRCColourQuality = (passthru?: string): string => (!passthru ? '16bpp' : passthru);

  /** Returns the default value for this property or, if specified, the passthru value */
  public defaultRCImageQuality = (passthru?: number): number => (!passthru ? 80 : passthru);

  /** Returns the default value for this property or, if specified, the passthru value */
  public defaultDeviceLogReportType = (passthru?: number): number =>
    passthru === undefined || passthru === null ? 0x01 : passthru;

  /** Returns the default value for this property or, if specified, the passthru value */
  public defaultDeviceLogCategory = (passthru?: string): string => (!passthru ? '' : passthru);

  /** Saves the agent video recording configuration */
  public updateAgentVideoRecordingPrefs = (
    value: IAgentVideoRecordingSettings
  ): Observable<IAgentVideoRecordingSettings> => {
    if (this.onUpdateAgentVideoRecordingPrefs) {
      this.onUpdateAgentVideoRecordingPrefs(value);
    }
    return of(value);
  };

  /** Sets a function to be called whenever the agent video recording settings change */
  public setOnUpdateAgentVideoRecordingPrefs(callback: (value: IAgentVideoRecordingSettings) => void): void {
    this.onUpdateAgentVideoRecordingPrefs = callback;
  }

  /** Accepts partial agent video recording settings and returns fully initialized
   * settings populating missing parameters with the defaults. */
  public defaultAgentVideoRecordingPrefs(
    settings: Partial<IAgentVideoRecordingSettings>
  ): IAgentVideoRecordingSettings {
    return {
      screenHeight: settings.screenHeight != null ? settings.screenHeight : 640,
      screenWidth: settings.screenWidth != null ? settings.screenWidth : 480,
      audioBitRate: settings.audioBitRate || 128000,
      audioSamplingRate: settings.audioSamplingRate || 44100,
      videoBitRate: settings.videoBitRate || 2500,
      videoFrameRate: settings.videoFrameRate || 20,
    } as IAgentVideoRecordingSettings;
  }

  // Set the RC Canvas parameters
  public setRCCanvasParameters = (rcCanvasConfig: IRCCanvasConfig): void => {
    if (RcContext.instance.session.appUI && rcCanvasConfig) {
      // const newColorQuality: number = ColorQuality.ColorQuality16Bpp;

      switch (rcCanvasConfig.colourQuality) {
        case '32bpp':
          RcContext.instance.session.appUI.setDeviceBitDepth(ColorQuality.ColorQuality32Bpp);
          RcContext.instance.session.appUI.setDeviceImageQuality(rcCanvasConfig.imageQuality);
          break;
        case '16bpp':
          RcContext.instance.session.appUI.setDeviceBitDepth(ColorQuality.ColorQuality16Bpp);
          RcContext.instance.session.appUI.setDeviceImageQuality(rcCanvasConfig.imageQuality);
          break;
        case '8bpp':
          RcContext.instance.session.appUI.setDeviceBitDepth(ColorQuality.ColorQuality8Bpp);
          RcContext.instance.session.appUI.setDeviceImageQuality(this.defaultRCImageQuality());
          break;
        case '4bpp':
          RcContext.instance.session.appUI.setDeviceBitDepth(ColorQuality.ColorQuality4Bpp);
          RcContext.instance.session.appUI.setDeviceImageQuality(this.defaultRCImageQuality());
          break;
        case '2bpp':
          RcContext.instance.session.appUI.setDeviceBitDepth(ColorQuality.ColorQuality2Bpp);
          RcContext.instance.session.appUI.setDeviceImageQuality(this.defaultRCImageQuality());
          break;
      }
    }
  };
}
